<template>
  <div class="wrapper">
    <div class="animated fadeIn">
      <b-row>
        <b-col cols="12">
          <div class="card">
            <div class="card-header">
              <i class="fa fa-align-justify"></i> Project {{ project_key }} issues
              <span class="badge badge-success ml-1" v-if="tableData.jira_import_enabled">
                <i class="fa fa-check" /> Importing
              </span>
              <span class="badge badge-default ml-1" v-if="tableData.jira_import_enabled">
                <i class="fa fa-check" /> Not Importing
              </span>
            </div>
            <div class="card-block">
              <b-row class="mb-1">
                <b-col cols="6">
                  <div>
                    <div>
                      <h5>
                        {{ tableData.name }} - {{ project_key }}
                      </h5>
                      <div>
                        <small>
                          <router-link :to="{ name: 'ProjectTabPlanning', params: { id: tableData.id } }">
                            Project in Contracts system <i class="fa fa-external-link fa-lg" />
                          </router-link>
                          <br />
                          <b-link :href="'https://applyit.atlassian.net/projects/' + tableData.key + ''">
                            Project in JIRA <i class="fa fa-external-link fa-lg" />
                          </b-link>

                          <b-checkbox v-model="showShallowEstimates">Show sub task estimates</b-checkbox>
                          <b-checkbox v-model="hideDoneTasks">
                            {{ $dt('project_issues.hide_done_checkbox', 'Hide done issues') }}
                            <b-badge v-if="doneTasksHidden > 0" variant="danger">
                              {{ doneTasksHidden }}
                              {{ $dt('project_issues.hiden_tasks_badge', 'tasks hidden') }}
                            </b-badge>
                          </b-checkbox>
                          <b-checkbox v-model="hideNoProgressTasks">
                            {{ $dt('project_issues.hide_no_progress_checkbox',
                              'Hide issues where progress is not tracked')
                            }}
                            <b-badge v-if="noProgressTasksHidden > 0" variant="danger">
                              {{ noProgressTasksHidden }}
                              {{ $dt('project_issues.hiden_tasks_badge', 'tasks hidden') }}
                            </b-badge>
                          </b-checkbox>
                          <b-checkbox v-model="hideMilestones">
                            {{ $dt('project_issues.hide_milestones_checkbox',
                              'Hide milestones')
                            }}
                            <b-badge v-if="milestonesHidden > 0" variant="danger">
                              {{ milestonesHidden }}
                              {{ $dt('project_issues.hiden_tasks_badge', 'tasks hidden') }}
                            </b-badge>
                          </b-checkbox>

                        </small>
                      </div>
                    </div>
                    <small>Last imported {{ tableData.last_imported_issues_parsed }}</small>
                  </div>
                </b-col>
              </b-row>
              <div v-if="spinners.contentIsLoading" class="text-center">
                <b-spinner type="grow" variant="primary" />
              </div>
              <div v-else>
                <b-row>
                  <b-col cols="12">
                    <b-table hover small :fields="tableFields" :items="tableItemsFiltered" sticky-header="100%">
                      <template v-slot:cell(jira_key)="row">
                        <div>
                          <!-- <b-link :href="'https://applyit.atlassian.net/projects/'+project_key+'/issues/'+row.item.jira_key"> -->
                          <b-link :href="'https://applyit.atlassian.net/browse/' + row.item.jira_key">
                            <span class="text-nowrap">{{ row.item.jira_key }}</span>
                          </b-link>
                        </div>
                      </template>
                      <template v-slot:cell(type)="row">
                        {{ row.item.type }}
                        <div>
                          <b-badge
                            :class="[`status-${row.item.status && row.item.status.category ? row.item.status.category.colorName : ''}`]">
                            {{ row.item.status ? row.item.status.name : '' }}
                          </b-badge>
                        </div>
                      </template>

                      <template v-slot:cell(summary)="row">
                        {{ row.item.summary }}
                        <div>
                          <small>
                            <b-link :href="'https://applyit.atlassian.net/jira/people/' + row.item.assignee_accountId">
                              {{ row.item.assignee_displayName }}
                            </b-link>
                          </small>
                        </div>
                      </template>
                      <template v-slot:cell(timeoriginalestimate)="row">
                        <div :class="{ 'text-zero': row.item.timeoriginalestimate == 0 }">{{
                          row.item.timeoriginalestimate
                        }}</div>
                        <div :class="[{
                              'text-danger': row.item.timeoriginalestimate < row.item.eac,
                              'text-warn': row.item.timeoriginalestimate == 0 && row.item.eac > 0,
                              'text-blue': row.item.timeoriginalestimate > row.item.eac && row.item.eac > 0,
                              'text-zero': row.item.eac == 0,
                              'text-success': row.item.eac > 0 && row.item.timeoriginalestimate > 0 && row.item.timeoriginalestimate >= row.item.eac,
                            }, 'small']">{{ row.item.eac }} h</div>
                      </template>
                      <template v-slot:cell(timestimate_shallow)="row">
                        <span :class="{ 'text-zero': row.item.timestimate_shallow == 0 }">{{
                          row.item.timestimate_shallow
                        }}</span>
                      </template>
                      <template v-slot:cell(timestimate_deep)="row">
                        <span :class="{ 'text-zero': row.item.timestimate_deep == 0 }">{{ row.item.timestimate_deep
                        }}</span>
                      </template>
                      <template v-slot:cell(actual_timespent)="row">
                        <span :class="{ 'text-zero': row.item.actual_timespent == 0 }">
                          {{ row.item.actual_timespent }}
                        </span>
                      </template>
                      <template v-slot:cell(gantt_progress)="row">
                        <div v-if="row.item.noprogress">
                          <b-badge>No progress</b-badge>
                          <div><b-badge variant="light" v-b-tooltip.hover
                              title="Untrackecked task progress % equals total progress of tracked tasks">{{
                                totalDonePercent }} %</b-badge></div>
                        </div>
                        <div v-else>{{ row.item.gantt_progress }} %</div>
                        <div v-if="row.item.milestone">
                          <b-badge>Milestone</b-badge>
                        </div>
                        <!-- <div class="small text-zero">{{ row.item.eac }} h</div> -->
                      </template>
                      <template v-slot:cell(etc)="row">
                        <div :class="['small', {
                          'font-weight-bold': row.item.isActive,
                          'text-zero': row.item.etc == 0,
                        }]">{{ row.item.etc }} h </div>
                        <div :class="['small', {
                          'font-weight-bold': row.item.isActive,
                        }]">
                          {{ row.item.gantt_from.format("DD.MM.YYYY") }}
                          - <span :class="{ 'text-danger': row.item.gantt_progress < 95 && row.item.isExpired }">
                            {{ row.item.gantt_to.format("DD.MM.YYYY") }}
                          </span>
                        </div>
                        <div class="small" v-if="row.item.avgLoad >= 0">
                          <span :class="['small', {
                            'text-zero': row.item.avgLoad === 0,
                          }]">{{ row.item.avgLoad }} fte </span>

                          <span v-if="row.item.avgLoad > 0">/ {{ row.item.weeks_remaining }} w</span>
                        </div>
                        <div class="small text-danger" v-else>
                          {{ $dt('jira_project_issues.unscheduled_error', 'Unschedduled (unnacounted hours)!') }}</div>
                      </template>
                      <template v-slot:cell(show_details)="row">
                        <b-button v-if="row.item.childIssuesCount" size="sm" @click="jiraToggleIssue(row)" class="mr-2">
                          {{ row.detailsShowing ? 'Hide' : 'Show' }} Details
                        </b-button>
                      </template>
                      <template v-slot:row-details="row">
                        <div v-if="spinners.issueIsLoading" class="text-center">
                          <b-spinner type="grow" variant="primary" />
                        </div>
                        <div v-else>
                          <b-card id="table-more-info">
                            <table class="table table-responsive  table-striped">
                              <tr>
                                <td class="border d-table-cell font-weight-bold" v-for="field in subTableFields"
                                  v-bind:key="field.key">
                                  {{ field.label }}
                                </td>
                              </tr>
                              <template v-for="(issue, index) in issue.data">
                                <tr v-bind:key="index" class="d-table-row">
                                  <td v-for="field in subTableFields" v-bind:key="field.key"
                                    :class="'border d-table-cell ' + field.tdClass">
                                    <span :class="{ 'text-zero': issue[field.key] == 0 }">
                                      <span v-if="field.key === 'jira_key'">
                                        <!-- <b-link :href="'https://applyit.atlassian.net/projects/'+project_key+'/issues/'+issue.jira_key"> -->
                                        <b-link :href="'https://applyit.atlassian.net/browse/' + issue.jira_key">
                                          <span class="text-nowrap">{{ issue.jira_key }}</span>
                                        </b-link>
                                      </span>
                                      <span v-else>
                                        {{ issue[field.key] }}
                                      </span>
                                      <div v-if="field.key === 'type'">
                                        <b-badge
                                          :class="[`status-${issue.status && issue.status.category ? issue.status.category.colorName : ''}`]">
                                          {{ issue.status ? issue.status.name : '' }}
                                        </b-badge>
                                      </div>
                                    </span>
                                    <div v-if="field.key === 'summary'">
                                      <small>
                                        <b-link
                                          :href="'https://applyit.atlassian.net/jira/people/' + issue.assignee_accountId">
                                          {{ issue.assignee_displayName }}
                                        </b-link>
                                      </small>
                                    </div>
                                  </td>
                                </tr>
                                <tr class="d-table-row" v-for="(childIssue, index) in issue.child_tasks"
                                  v-bind:key="issue.id">
                                  <td :class="'small border text-muted d-table-cell ' + field.tdClass"
                                    v-for="field in subTableFields" v-bind:key="field.key">
                                    <span :class="{ 'text-zero': childIssue[field.key] == 0 }">
                                      <span v-if="field.key === 'jira_key'">
                                        <!-- <b-link :href="'https://applyit.atlassian.net/projects/'+project_key+'/issues/'+childIssue.jira_key"> -->
                                        <b-link :href="'https://applyit.atlassian.net/browse/' + childIssue.jira_key">
                                          <span class="text-nowrap">{{ childIssue.jira_key }}</span>
                                        </b-link>
                                      </span>
                                      <span v-else>
                                        {{ childIssue[field.key] }}
                                      </span>
                                      <div v-if="field.key === 'type'">
                                        <b-badge
                                          :class="[`status-${childIssue.status && childIssue.status.category ? childIssue.status.category.colorName : ''}`]">
                                          {{ childIssue.status ? childIssue.status.name : '' }}
                                        </b-badge>
                                      </div>
                                      <div v-if="field.key === 'summary'">
                                        <small>
                                          <b-link
                                            :href="'https://applyit.atlassian.net/jira/people/' + issue.assignee_accountId">
                                            {{ issue.assignee_displayName }}
                                          </b-link>
                                        </small>
                                      </div>

                                    </span>

                                  </td>
                                </tr>
                              </template>
                              <tr class="bg-light d-table-row">
                                <template v-for="field in subTableFields">
                                  <td :class="'border d-table-cell ' + field.tdClass" v-bind:key="field.key">
                                    {{ issue.totals[field.key] }}
                                  </td>
                                </template>
                              </tr>

                            </table>

                          </b-card>
                        </div>
                      </template>
                      <template v-slot:bottom-row="data">
                        <td class="bg-light" colspan="3">
                          <b>Total:</b>
                        </td>
                        <td v-for="field in data.fields" v-bind:key="field.key" v-if="tableData.issues_totals[field.key]"
                          class="text-right text-monospace bg-light">
                          <span>{{ tableData.issues_totals[field.key] }}</span>
                          <div v-if="field.key === 'timeoriginalestimate'">EAC {{ totalEAC }}h</div>
                        </td>
                        <td class="bg-light">
                          <div>Total done {{ totalDonePercent }}%</div>
                          <!-- <div>EAC {{ totalEAC }}h</div> -->
                          <div>
                            <EditEstimate :jira_key="project_key" type="progress" />
                          </div>
                        </td>
                        <td class="bg-light">
                          <div>ETC {{ totalETC }}h</div>
                          <div>End date {{ endsAt ? endsAt.format('DD.MM.YYYY') : 'not known' }}</div>
                          <div>
                            <EditEstimate :jira_key="project_key" type="enddate" />
                          </div>
                        </td>
                        <td class="bg-light" colspan="3"></td>
                      </template>
                    </b-table>
                  </b-col>
                </b-row>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>

import { mapGetters, mapActions } from 'vuex';
import moment from 'moment';
import statuses from '../../../store/statuses';
import JiraTypes from '@/store/modules/Jira/types';
import Services from '@/services';
import EditEstimate from '@/views/components/project/EditEstimate.vue';

export default {
  mounted() {
    this.getData();
  },
  props: ['project_key'],
  components: {
    EditEstimate
  },
  data() {
    return {
      greentextclass: {
        color: '#ccc'
      },
      hideDoneTasks: true,
      hideNoProgressTasks: true,
      hideMilestones: true,
      spinners: {
        contentIsLoading: true,
        issueIsLoading: true,
        isChangingActive: false,
      },
      showShallowEstimates: false,
      issue: {
        opened: false,
        openedId: null,
        data: [],
        totals: []
      },
      tableItems: [],
      tableData: {},
      totalDonePercent: 0,
    };
  },
  computed: {
    ...mapGetters('Jira', [
      'jiraProjectIssues',
      'jiraIssueData',
      'jiraProjectsUpdate',
      'jiraProjectsPushUpdate',
      'jiraProjectsResetEntities'
    ]),
    tableItemsFiltered() {
      // filter done tasks if
      return this.tableItems.filter((i) => (
        (!(this.hideDoneTasks && i.gantt_progress === 100))
        && (!(this.hideNoProgressTasks && i.noprogress))
        && (!(this.hideMilestones && i.milestone))

      ));
      // return this.tableItems;
    },

    doneTasksHidden() {
      // how many done tasks are hidden
      return this.hideDoneTasks ? this.tableItems.reduce((cnt, i) => (i.gantt_progress === 100 ? cnt + 1 : cnt), 0) : 0;
    },
    noProgressTasksHidden() {
      // how many done tasks are hidden
      return this.hideNoProgressTasks ? this.tableItems.reduce((cnt, i) => (i.noprogress ? cnt + 1 : cnt), 0) : 0;
    },
    milestonesHidden() {
      return this.hideMilestones ? this.tableItems.reduce((cnt, i) => (i.milestone ? cnt + 1 : cnt), 0) : 0;
    },

    list() {
      return this.jiraProjectIssues;
    },
    totalETC() {
      return this.tableItems.reduce((hours, i) => {
        return hours + i.etc;
      }, 0);
    },
    totalEstimate() {
      return this.tableItems.reduce((hours, i) => {
        return hours + 1 * i.timeoriginalestimate;
      }, 0);
    },
    totalEAC() {
      return this.tableItems.reduce((hours, i) => {
        return hours + 1 * i.eac;
      }, 0);
    },
    endsAt() {
      return this.tableItems.reduce((m, i) => {
        return (m && i.gantt_to) ? moment.max(m, i.gantt_to) : i.gantt_to || m;
      }, null);
    },
    // totalDonePercent() {
    //   // EAC sum for tasks with progress tracked (discluding no progress tasks)
    //   const eacTotal = this.tableItems.filter(i => !i.noprogress).reduce((hours, i) => {
    //     return hours + 1 * i.eac;
    //   }, 0);
    //   // ETC sum for tasks with progress tracked (discluding no progress tasks)
    //   const etcTotal = this.tableItems.filter(i => !i.noprogress).reduce((hours, i) => {
    //     return hours + 1 * i.etc;
    //   }, 0);
    //   return Math.round((eacTotal - etcTotal) / eacTotal * 1000) / 10;
    //   // return Math.round((this.totalEAC - this.totalETC) / this.totalEAC * 1000) / 10;
    // },

    tableFields() {
      return [
        { key: 'jira_key', label: 'Key', tdClass: 'align-middle text-center' },
        { key: 'type', label: 'Tips', tdClass: 'align-middle text-center' },
        { key: 'summary', label: 'Summary', tdClass: 'small text-left' },
        { key: 'timeoriginalestimate', label: 'Estimate/EAC', class: 'text-right', tdClass: 'text-monospace' },
        ...(this.showShallowEstimates ? [
          { key: 'timestimate_shallow', label: 'Estimate T', class: 'text-right', tdClass: 'text-monospace' },
          { key: 'timestimate_deep', label: 'Estimate T+ST', class: 'text-right', tdClass: 'text-monospace' },
        ] : []),
        { key: 'actual_timespent', label: 'Spent', class: 'text-right', tdClass: 'text-monospace' },
        { key: 'gantt_progress', label: 'Percent', class: 'text-right', tdClass: 'text-monospace' },
        { key: 'etc', label: 'ETC', class: 'text-right', tdClass: 'text-monospace' },
        { key: 'show_details', label: 'Details', class: 'text-center' },
      ];
    },
    subTableFields() {
      return [
        { key: 'jira_key', label: 'Key' },
        { key: 'type', label: 'Type', tdClass: 'align-vertical' },
        { key: 'summary', label: 'Summary', tdClass: 'small text-left' },
        { key: 'timeoriginalestimate', label: 'Estimate', tdClass: 'text-right text-monospace' },
        ...(this.showShallowEstimates ? [
          { key: 'timestimate_shallow', label: 'Estimate ST', tdClass: 'text-right text-monospace' },
        ] : []),
        { key: 'actual_timespent', label: 'Spent', tdClass: 'text-right text-monospace' },
      ];
    },
  },
  watch: {
    jiraIssueData: {
      handler: function () {
        if (this.jiraIssueData['status'] === statuses.LOADED) {
          this.spinners.issueIsLoading = false;
          this.issue.data = this.jiraIssueData.data.data || [];
          this.issue.totals = this.jiraIssueData.data.totals || [];
        }
      },
      deep: true
    },
    jiraProjectsUpdate: {
      handler: function () {
        if (this.jiraProjectsUpdate.status === statuses.LOADED) {
          this.spinners.isChangingActive = false;
          // this.alertSuccess();
          this.getData();
        } else if (this.jiraProjectsUpdate.status === statuses.ERROR) {
          this.spinners.isChangingActive = false;
          // this.alertError();
        }
      },
      deep: true
    },
    jiraProjectsPushUpdate: {
      handler: function () {
        if (this.jiraProjectsPushUpdate.status === statuses.LOADED) {
          this.spinners.isChangingActive = false;
          // this.alertSuccess();
          this.getData();
        } else if (this.jiraProjectsPushUpdate.status === statuses.ERROR) {
          this.spinners.isChangingActive = false;
          // this.alertError();
        }
      },
      deep: true
    },
    jiraProjectsResetEntities: 'getData',
  },
  methods: {
    ...mapActions('Jira', [
      // JiraTypes.JIRA_PROJECT_ISSUES,
      JiraTypes.JIRA_ISSUE_DATA,
      JiraTypes.JIRA_PROJECTS_UPDATE,
      JiraTypes.JIRA_PROJECTS_PUSH_UPDATE,
      JiraTypes.JIRA_PROJECTS_RESET_ENTITIES
    ]),
    getData() {
      Services.jira.projectIssues({ project_key: this.$props.project_key, without_ignore_progress: true }).then(({ data }) => {
        this.tableData = data;

        let tableItems = (data.issues.map((i) => {
          // eslint-disable-next-line no-underscore-dangle
          if (!['Task', 'Epic', 'Meeting'].includes(i.type)) i._rowVariant = 'danger';
          return i;
        }) || []).map((i) => {
          // If task state is done set gant progress to 100%
          if (i.status && ["Done", "Dev is done", "Closed", "Ready to Accept", "Canceled", "Accepted by client"].includes(i.status.name)) i.gantt_progress = 100;
          const minPercentTrack = 5;
          const shouldHaveDone = (Math.max(1 * i.gantt_progress, minPercentTrack) / 100) * i.timeoriginalestimate;
          i.noprogress = !!i.labels.find((l) => l.label === 'noprogress');
          i.milestone = !!i.labels.find((l) => l.label === '#milestone');
          i.eac = Math.round(
            (i.gantt_progress < minPercentTrack || (i.actual_timespent < 1 && i.gantt_progress < 100))
              ? ((1 - i.gantt_progress / 100) * i.timeoriginalestimate + ((i.actual_timespent < shouldHaveDone) ? 0 : 1 * i.actual_timespent))
              : (i.actual_timespent === 0)
                ? 1 * i.timeoriginalestimate
                : ((i.actual_timespent / i.gantt_progress) * 100)
          );
          i.etc = Math.max(0, Math.round(i.eac - Math.round(i.actual_timespent)));
          i.gantt_from = moment(i.gantt_from);
          i.gantt_to = moment(i.gantt_to);
          return i;
        }).sort((a, b) => {
          return (!a.gantt_from || (b.gantt_from && a.gantt_from > b.gantt_from)) ? 1
            : (!b.gantt_from || (a.gantt_from && a.gantt_from < b.gantt_from)) ? -1
              : 0;
        });

        // EAC,ETC sum for tasks with progress tracked (tasks with label noprgress are filtered out)
        const [eacTotal, etcTotal] = tableItems.filter(i => !i.noprogress).reduce(([eacTotal, etcTotal], i) => {
          return [eacTotal + 1 * i.eac, etcTotal + 1 * i.etc];
        }, [0, 0]);
        this.totalDonePercent = Math.round((eacTotal - etcTotal) / eacTotal * 1000) / 10;

        tableItems = tableItems.map((i) => {
          // For no-progress tasks set progress  = totalDonePercent
          if (i.noprogress) {
            i.gantt_progress = this.totalDonePercent;
            i.eac = i.gantt_progress > 0 ? Math.round((i.actual_timespent / i.gantt_progress) * 100) : i.timeoriginalestimate;
            i.etc = Math.max(0, Math.round(i.eac - Math.round(i.actual_timespent)));
          }

          i.isActive = i.gantt_from.isBefore(moment()) && i.gantt_to.endOf('week').isAfter(moment());
          i.isExpired = i.gantt_to.isBefore(moment());
          i.weeks_remaining = i.gantt_to.startOf('week').add(1, 'weeks').diff(moment.max(moment(i.gantt_from), moment()).startOf('week'), 'days') / 7;
          i.avgLoad = (i.weeks_remaining <= 0 && i.etc > 0) ? -1 : Math.round(((i.etc / i.weeks_remaining) / 40) * 10) / 10 || 0;
          if ((i.eac > i.timeoriginalestimate * 1.1)
            && (i.eac - i.timeoriginalestimate > 8)
          ) {
            // eslint-disable-next-line no-underscore-dangle
            i._rowVariant = 'danger';
          }
          return i;
        })

        this.tableItems = tableItems;
        this.spinners.contentIsLoading = false;
      }).catch((err) => {
        console.warn(err);
      });

      // this.JIRA_PROJECT_ISSUES({
      //   project_key: this.$props.project_key,
      // });
    },
    jiraToggleIssue(row) {
      if (this.issue.openedId === row.item.id) { // self close
        this.$set(row.item, '_showDetails', false);
        this.issue.openedId = null;
      } else {
        if (this.issue.openedId !== null) { // close every other
          this.tableItems.forEach((tableRow) => {
            if (tableRow.id === this.issue.openedId) {
              this.$set(tableRow, '_showDetails', false);
              this.issue.openedId = null;
            }
          });
        }

        // open
        this.spinners.issueIsLoading = true;
        this.issue.opened = true;
        this.issue.openedId = row.item.id;
        this.$set(row.item, '_showDetails', true);
        this.JIRA_ISSUE_DATA(row.item.id);
      }
    },
    jiraProjectToggle(id, isActive) {
      this.spinners.isChangingActive = true;
      this.JIRA_PROJECTS_UPDATE({ id, isActive });
    },
    jiraPushProjectUpdate(id) {
      this.JIRA_PROJECTS_PUSH_UPDATE(id);
    },
    jiraProjectReset(id) {
      this.JIRA_PROJECTS_RESET_ENTITIES(id);
    }
  },
};
</script>

<style scoped lang=scss>
#table-more-info {
  border: 0;
}

#table-more-info .card-body {
  padding: 0;
}

#table-more-info table tr {
  background-color: #fff;
}

#table-more-info.card {
  margin-bottom: 0;
}

#table-more-info table {
  padding: 5px;
  margin-bottom: 0;
  display: table;
}

/*.b-table-details {
    border-left: 5px solid #20a8d8;
    border-right: 5px solid #20a8d8;
  }*/

.greentextclass {
  color: red;
}

.text-blue {
  color: rgb(10, 63, 255);
}

.text-zero {
  color: rgb(142, 142, 142);
}
</style>
